import React from "react";
import { sb } from "../lib/supabase";
import {
  Player,
  Power,
  AuraType,
  Aura,
  LeagueMatchRecord,
  LeagueMember,
  MemberStats,
  LeagueCardState,
  FieldAsset,
} from "../types/game";
import { cStats } from "../engine/playerEngine";
import {
  initCardState,
  ensureDailyShop,
  rollShop,
  getActiveCombos,
  computeCardModifiers,
  ComboActivation,
} from "../engine/shopEngine";
import {
  CARD_BY_ID,
  CARD_COST,
  REROLL_COST,
  STARTER_LEAGUE_GOLD,
  MATCH_GOLD_REWARD,
  ORB_COST,
  MAX_INVENTORY,
  SELL_REFUND,
} from "../constants/cards";
import {
  simulateMatch,
  MatchAction,
  PlayerRating,
  MatchStatsSheet,
  MatchSimResult,
} from "../engine/matchEngine";
import {
  generateBotTeam,
  computeGoalkeeperLevel,
  GK_UPGRADE_COST,
  getCountdownLabel,
  generateWeekSchedule,
  isMatchDue,
  computeWeekTable,
  TeamStanding,
  FixtureOutcome,
} from "../engine/leagueEngine";
import { craftPower, PREMIUM_RECIPES, makePremiumPower } from "../constants/crafting";
import {
  getTier,
  getLeagueWeekStart,
  daysUntilLeagueWeekEnd,
  MATCHES_PER_WEEK,
  LeagueTier,
} from "../constants/league";

const ALL_AURA_TYPES: AuraType[] = ["ROUGE", "BLEUE", "VERTE"];
const ALLIED_BOT_POWER = 30;
const BOT_NAMES = [
  "Zlatan_Bot", "Kiki_Bot", "Ney_Bot", "CR7_Bot", "Messi_Bot",
  "Roni_Bot",   "Zizou_Bot","Titi_Bot","Choupo_Bot","Balotelli_Bot",
];

function todayOpponentSeed(): number {
  const d = new Date();
  return d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
}

function nextMatchAt(): string {
  const d = new Date();
  d.setHours(16, 0, 0, 0);
  if (Date.now() >= d.getTime()) d.setDate(d.getDate() + 1);
  return d.toISOString();
}

/* ─── Performance en match = 40% items équipés + 60% cards & pouvoirs ────────
   La force d'un joueur en match de ligue ne dépend plus à 100% de ses items :
   les items pèsent 40%, et les ATOUTS de match (carte posée sur le terrain +
   pouvoirs détenus) pèsent 60%. Tous les boutons de réglage sont ici. */
const PERF = {
  scale: 240, // puissance d'un joueur "complet" (items maxés + atouts maxés)
  itemsWeight: 0.4, // part des items équipés
  assetsWeight: 0.6, // part des cards + pouvoirs
  itemsRef: 160, // power d'items considéré comme "complet"
  assetsRef: 90, // score cards+pouvoirs considéré comme "complet"
  cardTierValue: { 1: 8, 2: 18, 3: 30, 4: 46 } as Record<number, number>,
};

/** Force d'un joueur en match : 40% items + 60% (carte de terrain + pouvoirs). */
function leaguePerformance(itemsPower: number, u: Record<string, unknown>): number {
  const cards = u.league_cards as LeagueCardState | undefined;
  let assetScore = 0;
  const activeCard = cards?.active?.[0];
  if (activeCard && CARD_BY_ID[activeCard]) {
    assetScore += PERF.cardTierValue[CARD_BY_ID[activeCard].tier] ?? 0;
  }
  for (const p of (u.league_powers as Power[] | undefined) ?? []) {
    assetScore += Number(p.value) || 0;
  }
  const active = u.active_power as Power | null | undefined;
  if (active) assetScore += Number(active.value) || 0;

  const itemsNorm = Math.min(1, Math.max(0, itemsPower) / PERF.itemsRef);
  const assetsNorm = Math.min(1, assetScore / PERF.assetsRef);
  return Math.round(
    PERF.scale * (PERF.itemsWeight * itemsNorm + PERF.assetsWeight * assetsNorm)
  );
}

/** Cache module-level des dernières données chargées : survit au démontage du
 *  composant Ligue (changement d'onglet) → on réaffiche instantanément sans
 *  repasser par le skeleton. Rafraîchi en arrière-plan à chaque chargement. */
let leagueCache: { members: LeagueMember[]; frozen: FrozenFixture[] } | null = null;

/** Match figé en base (table `league_fixtures`) — du point de vue de mon équipe. */
export interface FrozenFixture {
  round: number;
  team_key: string;
  team_name: string;
  opp_key: string;
  opp_name: string;
  team_power: number;
  opp_power: number;
  result: "win" | "draw" | "loss";
  home_score: number;
  away_score: number;
  mvp?: string;
  events?: MatchAction[];
  ratings?: PlayerRating[];
  stats?: MatchStatsSheet;
}

export interface ScheduleEntry {
  day: number; // 0-6
  scheduledAt: string;
  opponentName: string;
  opponentSeed: number; // seed déterministe de l'équipe adverse
  oppPower: number;
  due: boolean; // match déjà joué (date passée)
}

export interface UseLeagueReturn {
  hasJoined: boolean;
  leagueMembers: LeagueMember[];
  loading: boolean;
  ready: boolean; // données chargées au moins une fois (anti-flicker)
  standings: TeamStanding[]; // classement complet de la poule (cohérent)
  myRank: number;
  resultByRound: Record<number, "win" | "draw" | "loss">; // résultat de mes matchs par journée
  scoreByRound: Record<number, { gf: number; ga: number }>; // score de mes matchs par journée
  fixtureByRound: Record<number, FrozenFixture>; // fixture figée (timeline + stats) par journée

  currentMatch: LeagueMatchRecord | null;
  matchHistory: LeagueMatchRecord[];
  countdown: string;
  matchSecondsElapsed: number;
  showRatingsPopup: boolean;
  setShowRatingsPopup: (v: boolean) => void;
  totalTeamPower: number;
  realPlayersCount: number;
  botCount: number;
  goalkeeperLevel: number;
  wins: number;
  draws: number;
  losses: number;
  played: number;
  points: number;
  winrate: number;
  timeline: MatchAction[];
  ratings: PlayerRating[];
  activePower: Power | null;
  setActivePower: (p: Power | null) => void;
  joinLeague: (teamName?: string) => Promise<void>;
  runMatch: () => Promise<void>;
  runTestMatch: (powerId?: string) => MatchSimResult;
  upgradeGoalkeeper: (amount: number) => Promise<void>;
  craftAuras: (a: AuraType, b: AuraType) => Power | null;
  loadLeagueData: () => Promise<void>;
  /* ─── Mode Ligue : orbes / pouvoirs / calendrier / palier ─── */
  orbCounts: Record<AuraType, number>;
  powers: Power[]; // inventaire des pouvoirs (non assignés inclus)
  matchPowers: Record<number, string>;
  schedule: ScheduleEntry[];
  preparedCount: number;
  elo: number;
  tier: LeagueTier;
  tierIndex: number;
  nextTier: LeagueTier | null;
  daysLeft: number;
  matchesDone: number;
  craftPowerFromOrbs: (a: AuraType, b: AuraType) => Power | null;
  craftPremiumRecipe: (recipeId: string) => Power | null;
  assignPowerToMatch: (matchIndex: number, powerId: string) => void;
  unassignMatch: (matchIndex: number) => void;
  /* ─── Économie de cartes (système TFT) ─── */
  leagueGold: number;
  cardState: LeagueCardState;
  fieldAssets: FieldAsset[]; // atouts de toute l'équipe sur le terrain
  teamCombos: ComboActivation[]; // synergies débloquées par l'équipe
  buyCard: (cardId: string) => void;
  sellCard: (cardId: string) => void; // revend une carte de l'inventaire
  buyOrb: (type: AuraType) => boolean; // achète une orbe contre des Golds
  rerollShop: () => void;
  sendToField: (cardId: string) => void; // envoyer / retirer son atout
  setCardsLocked: (locked: boolean) => void;
  leaveLeague: () => void;
}

export function useLeague(
  player: Player | null,
  savePlayer: (p: Player) => void,
  bettingPoints = 0
): UseLeagueReturn {
  const [hasJoined, setHasJoined] = React.useState(
    !!player?.has_joined_league
  );
  // Restaure les données déjà chargées (cache module) → pas de skeleton au
  // remontage du composant Ligue quand on change d'onglet et qu'on revient.
  const cached = leagueCache;
  const [leagueMembers, setLeagueMembers] = React.useState<LeagueMember[]>(
    cached?.members ?? []
  );
  // Tick toutes les minutes pour mettre à jour `due` des matchs en temps réel.
  const [now, setNow] = React.useState(() => new Date());
  React.useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);
  const [loading, setLoading] = React.useState(false);
  // Données membres chargées au moins une fois → autorise l'affichage du
  // classement (évite d'afficher des chiffres faux tant que la puissance
  // d'équipe vaut 0 pendant le chargement initial).
  const [ready, setReady] = React.useState(!!cached);
  // Mes matchs FIGÉS en base pour la semaine en cours (résultat + récit).
  const [frozen, setFrozen] = React.useState<FrozenFixture[]>(
    cached?.frozen ?? []
  );
  const [currentMatch, setCurrentMatch] =
    React.useState<LeagueMatchRecord | null>(null);
  // Initialisé au premier chargement pour ne pas re-créditer un match déjà connu.
  const initialLoadDone = React.useRef(false);
  const isLoadingRef = React.useRef(false); // guard React StrictMode double-invoke
  const [matchHistory, setMatchHistory] = React.useState<LeagueMatchRecord[]>(
    []
  );
  const [countdown, setCountdown] = React.useState("");
  const [matchSecondsElapsed, setMatchSecondsElapsed] = React.useState(-1);
  const [showRatingsPopup, setShowRatingsPopup] = React.useState(false);
  const [activePower, setActivePower] = React.useState<Power | null>(
    player?.active_power ?? null
  );

  const isSimRef = React.useRef(false);
  const simSecRef = React.useRef(0);
  const seededRef = React.useRef(false);
  // Dernier match ID déjà récompensé — évite le double crédit au refresh.
  const lastRewardedMatchRef = React.useRef<string | number | null>(null);

  React.useEffect(() => {
    setHasJoined(!!player?.has_joined_league);
  }, [player?.has_joined_league]);

  // ── Seed : 1 orbe de chaque couleur pour un joueur ligue sans orbe ──────
  React.useEffect(() => {
    if (seededRef.current) return;
    if (!player || !hasJoined) return;
    if ((player.auras ?? []).length > 0) {
      seededRef.current = true;
      return;
    }
    seededRef.current = true;
    savePlayer({
      ...player,
      auras: [
        { id: `orb_${Date.now()}_r`, type: "ROUGE" },
        { id: `orb_${Date.now()}_b`, type: "BLEUE" },
        { id: `orb_${Date.now()}_v`, type: "VERTE" },
      ],
    });
  }, [hasJoined, player, savePlayer]);

  // ── Countdown ticker ────────────────────────────────────────────────────
  React.useEffect(() => {
    const tick = () => {
      if (isSimRef.current) {
        simSecRef.current += 1;
        if (simSecRef.current >= 60) {
          isSimRef.current = false;
          setMatchSecondsElapsed(-1);
          setShowRatingsPopup(true);
        } else {
          setMatchSecondsElapsed(simSecRef.current);
          setCountdown("MATCH EN COURS ⚽");
        }
        return;
      }
      setCountdown(getCountdownLabel(nextMatchAt()));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  // ── Auto-refresh every 30 s when joined ────────────────────────────────
  React.useEffect(() => {
    if (!hasJoined) return;
    const id = setInterval(loadLeagueData, 30_000);
    return () => clearInterval(id);
  }, [hasJoined]); // eslint-disable-line

  const loadLeagueData = React.useCallback(async () => {
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;
    setLoading(true);

    // ── 1. Membres ──────────────────────────────────────────────────────────
    let newMembers: LeagueMember[] = [];
    try {
      const rawPlayers: unknown[] = await sb.get("players");
      if (Array.isArray(rawPlayers)) {
        const filtered = rawPlayers.filter(
          (u: unknown) =>
            (u as Record<string, unknown>).has_joined_league === true ||
            (u as Record<string, unknown>).has_joined_league === "true" ||
            (u as Record<string, unknown>).has_joined_league === 1
        ) as Array<Record<string, unknown>>;
        const real: LeagueMember[] = filtered.map((u) => {
          const cards = u.league_cards as LeagueCardState | undefined;
          const isMe = u.name === player?.name;
          const power = leaguePerformance(Number(u.power) || ALLIED_BOT_POWER, u);
          // Pour MOI, on dérive le profil 4-stats de mes items équipés (cStats),
          // remis à l'échelle de ma puissance de match (items 40% + atouts 60%).
          let stats: MemberStats | undefined;
          if (isMe && player) {
            const cs = cStats(player);
            const raw = cs.technique + cs.defense + cs.physique + cs.vitesse;
            if (raw > 0) {
              const k = power / raw;
              stats = {
                technique: cs.technique * k,
                defense: cs.defense * k,
                physique: cs.physique * k,
                vitesse: cs.vitesse * k,
              };
            }
          }
          return {
            name: String(u.name || u.username || "Joueur"),
            power,
            stats,
            isMe,
            isBot: false,
            sentCardId: cards?.active?.[0],
          };
        });
        const botsNeeded = Math.max(0, 11 - real.length);
        const bots: LeagueMember[] = BOT_NAMES.slice(0, botsNeeded).map(
          (name) => ({ name, power: ALLIED_BOT_POWER, isMe: false, isBot: true })
        );
        newMembers = [...real, ...bots].sort((a, b) => b.power - a.power);
      }
    } catch (e) {
      console.warn("useLeague: erreur chargement membres", e);
    }

    // ── 2. Matchs ligue (historique) ────────────────────────────────────────
    try {
      const rawMatches: unknown[] = await sb.get(
        "league_matches?select=*&order=created_at.desc&limit=10"
      );
      if (Array.isArray(rawMatches) && rawMatches.length > 0) {
        const [latest, ...rest] = rawMatches as LeagueMatchRecord[];
        setCurrentMatch(latest);
        setMatchHistory(rest);
        if (!initialLoadDone.current) {
          initialLoadDone.current = true;
          lastRewardedMatchRef.current = latest.id;
        } else if (player && latest.id !== lastRewardedMatchRef.current) {
          lastRewardedMatchRef.current = latest.id;
          const goldReward = MATCH_GOLD_REWARD[latest.result];
          const newAuras: Aura[] = [
            ...(player.auras ?? []),
            { id: `orb_${Date.now()}_r`, type: "ROUGE" },
            { id: `orb_${Date.now() + 1}_b`, type: "BLEUE" },
          ];
          const usedPowerIds = new Set(Object.values(player.match_powers ?? {}));
          const remainingPowers = (player.league_powers ?? []).filter(
            (p) => !usedPowerIds.has(p.id)
          );
          // Patch ciblé : uniquement les colonnes League classique modifiées ici.
          // Ne pas utiliser savePlayer({...player}) — le setInterval tient une référence
          // stale de loadLeagueData, donc player.match_gold / inventory / equipped / duel_*
          // seraient des anciens snapshots qui écraseraient les valeurs DB à jour.
          sb.patch("players", `name=eq.${encodeURIComponent(player.name)}`, {
            league_gold: (player.league_gold ?? STARTER_LEAGUE_GOLD) + goldReward,
            auras: newAuras,
            league_powers: remainingPowers,
            match_powers: {},
          });
        }
      }
    } catch (e) {
      console.warn("useLeague: erreur chargement matchs", e);
    }

    // ── 3. Fixtures gelées — chargement + simulation des matchs manquants ───
    // Tout se fait ici avec les valeurs locales (newMembers) pour éviter un
    // second render après setReady. Un seul render à la fin = pas de flicker.
    let newFrozen: FrozenFixture[] = [];
    if (player?.has_joined_league && player?.name && newMembers.length > 0) {
      const localPower = newMembers.reduce((s, m) => s + m.power, 0);
      const localGkLevel = computeGoalkeeperLevel(
        newMembers.filter((m) => !m.isBot).map((m) => ({
          goalkeeper_contribution:
            m.name === player.name ? (player.goalkeeper_contribution ?? 0) : 0,
        }))
      );
      const wkIso = getLeagueWeekStart().toISOString();
      const teamKey = `team:${player.league_team ?? "Hyundai FC"}`;
      const teamName = player.league_team ?? "Hyundai FC";

      try {
        const raw = await sb.get(
          `league_fixtures?week_start=eq.${encodeURIComponent(wkIso)}&team_key=eq.${encodeURIComponent(teamKey)}&select=*`
        );
        if (Array.isArray(raw)) newFrozen = raw as FrozenFixture[];
      } catch { /* table absente → degraded mode */ }

      const have = new Set(newFrozen.map((r) => r.round));
      const inserts: FrozenFixture[] = [];
      const nowMs = Date.now();
      for (const d of generateWeekSchedule(1, wkIso)) {
        if (nowMs < new Date(d.scheduledAt).getTime()) continue;
        const seed = d.opponentSeed + d.day;
        if (have.has(d.day)) continue;
        const oppTeam = generateBotTeam(1, seed);
        const oppPower = oppTeam.members.reduce((s, m) => s + m.power, 0);
        const assignedId = player.match_powers?.[d.day];
        const activeForMatch =
          (player.league_powers ?? []).find((p) => p.id === assignedId) ?? null;
        // Seed déterministe → résultat stable même en mode dégradé (sans table,
        // syncMyFixtures re-simule à chaque chargement : pas de flicker).
        const matchSeed = Math.abs((seed * 131071 + localPower * 31 + oppPower) | 0);
        const sim = simulateMatch(
          { name: teamName, members: newMembers, goalkeeperLevel: localGkLevel, activePower: activeForMatch },
          { name: oppTeam.name, members: oppTeam.members, goalkeeperLevel: oppTeam.goalkeeperLevel },
          matchSeed
        );
        inserts.push({
          round: d.day, team_key: teamKey, team_name: teamName,
          opp_key: `bot:${seed}`, opp_name: oppTeam.name,
          team_power: localPower, opp_power: oppPower,
          result: sim.result, home_score: sim.homeScore, away_score: sim.awayScore,
          mvp: sim.mvp, events: sim.timeline, ratings: sim.ratings, stats: sim.stats,
        });
      }
      if (inserts.length) {
        try {
          await sb.insert("league_fixtures", inserts.map((f) => ({ ...f, week_start: wkIso })));
        } catch { /* noop */ }
        newFrozen = [...newFrozen, ...inserts];
      }
    }

    // ── 4. Un seul batch de setState → un seul render ───────────────────────
    leagueCache = { members: newMembers, frozen: newFrozen };
    setLeagueMembers(newMembers);
    setFrozen(newFrozen);
    setLoading(false);
    setReady(true);
    isLoadingRef.current = false;
  }, [player?.name, player?.has_joined_league, player?.league_team, player?.match_powers, player?.league_powers, player?.goalkeeper_contribution]);

  React.useEffect(() => {
    loadLeagueData();
  }, []); // eslint-disable-line

  // ── Computed values ─────────────────────────────────────────────────────
  const totalTeamPower = leagueMembers.reduce((s, m) => s + m.power, 0);
  const realPlayersCount = leagueMembers.filter((m) => !m.isBot).length;
  const botCount = leagueMembers.filter((m) => m.isBot).length;

  const goalkeeperLevel = computeGoalkeeperLevel(
    leagueMembers
      .filter((m) => !m.isBot)
      .map((m) => ({
        goalkeeper_contribution:
          m.name === player?.name
            ? (player?.goalkeeper_contribution ?? 0)
            : 0,
      }))
  );

  // ── Orbes / pouvoirs / calendrier / palier ──────────────────────────────
  const orbCounts = React.useMemo<Record<AuraType, number>>(() => {
    const auras = player?.auras ?? [];
    return ALL_AURA_TYPES.reduce(
      (acc, t) => ({ ...acc, [t]: auras.filter((a) => a.type === t).length }),
      { ROUGE: 0, BLEUE: 0, VERTE: 0 }
    );
  }, [player?.auras]);

  const powers = player?.league_powers ?? [];
  const matchPowers = player?.match_powers ?? {};
  const preparedCount = Object.keys(matchPowers).length;

  const elo = player?.league_elo ?? 1000;
  const { tier, index: tierIndex, next: nextTier } = getTier(elo);
  const daysLeft = daysUntilLeagueWeekEnd();

  const schedule = React.useMemo<ScheduleEntry[]>(() => {
    const weekStart = getLeagueWeekStart(now).toISOString();
    return generateWeekSchedule(1, weekStart).map((d) => {
      const seed = d.opponentSeed + d.day;
      const opp = generateBotTeam(1, seed);
      return {
        day: d.day,
        scheduledAt: d.scheduledAt,
        opponentName: opp.name,
        opponentSeed: seed,
        oppPower: opp.members.reduce((s, m) => s + m.power, 0),
        due: now.getTime() >= new Date(d.scheduledAt).getTime(),
      };
    });
  }, [now]);

  const myTeamName = player?.league_team ?? "Hyundai FC";
  const weekStartIso = getLeagueWeekStart().toISOString();
  const myKey = `team:${myTeamName}`;


  // Résultats figés indexés par journée → injectés dans le round-robin.
  const frozenMap = React.useMemo(() => {
    const m = new Map<number, FixtureOutcome>();
    for (const f of frozen) {
      m.set(f.round, {
        result: f.result,
        homeScore: f.home_score,
        awayScore: f.away_score,
        points: f.result === "win" ? 3 : f.result === "draw" ? 1 : 0,
      });
    }
    return m;
  }, [frozen]);

  // ── Mini-ligue hebdo cohérente (round-robin déterministe + matchs figés) ──
  // Une seule source de vérité : toute la poule (moi + mes 7 adversaires) joue
  // un round-robin, chacun marque ses points avec la même règle. MES matchs
  // utilisent le résultat FIGÉ (jamais recalculé) ; les bots restent calculés.
  const weekTable = React.useMemo(
    () => computeWeekTable(totalTeamPower, myTeamName, weekStartIso, frozenMap),
    [totalTeamPower, myTeamName, weekStartIso, frozenMap]
  );
  const standings = weekTable.standings;
  const myRank = weekTable.myRank;
  const matchesDone = weekTable.matchesDone;

  // Résultat de CHACUN de mes matchs, indexé par journée (round) — même source
  // que le classement (round-robin figé). LeaguePrep s'en sert pour afficher
  // les matchs "finished" en cohérence parfaite avec le standing.
  const resultByRound = React.useMemo<Record<number, "win" | "draw" | "loss">>(() => {
    const me = standings.find((t) => t.isMe);
    const out: Record<number, "win" | "draw" | "loss"> = {};
    if (me) {
      for (const f of me.fixtures) {
        if (f.result) out[f.round] = f.result;
      }
    }
    return out;
  }, [standings]);

  // Fixture figée (timeline + stats) indexée par journée → Feuille de match.
  const fixtureByRound = React.useMemo<Record<number, FrozenFixture>>(() => {
    const out: Record<number, FrozenFixture> = {};
    for (const f of frozen) out[f.round] = f;
    return out;
  }, [frozen]);

  // Score de CHACUN de mes matchs, indexé par journée — même source figée.
  const scoreByRound = React.useMemo<Record<number, { gf: number; ga: number }>>(() => {
    const me = standings.find((t) => t.isMe);
    const out: Record<number, { gf: number; ga: number }> = {};
    if (me) {
      for (const f of me.fixtures) {
        if (f.result && f.goalsFor != null && f.goalsAgainst != null) {
          out[f.round] = { gf: f.goalsFor, ga: f.goalsAgainst };
        }
      }
    }
    return out;
  }, [standings]);

  const { wins, draws, losses, played, points, winrate } = React.useMemo(() => {
    const me = standings.find((t) => t.isMe);
    if (!me) {
      return { wins: 0, draws: 0, losses: 0, played: 0, points: 0, winrate: 0 };
    }
    return {
      wins: me.wins,
      draws: me.draws,
      losses: me.losses,
      played: me.played,
      points: me.points,
      winrate: me.played > 0 ? Math.round((me.wins / me.played) * 100) : 0,
    };
  }, [standings]);

  // ── Économie de cartes (système TFT) ─────────────────────────────────────
  const userKey = player?.name ?? "anon";
  const leagueGold = player?.league_gold ?? STARTER_LEAGUE_GOLD;

  // Vue cohérente du shop (toujours alignée sur le jour courant).
  const cardState = React.useMemo<LeagueCardState>(() => {
    const base = player?.league_cards ?? initCardState(userKey);
    return ensureDailyShop(base, userKey);
  }, [player?.league_cards, userKey]);

  // Atouts posés sur le terrain d'équipe partagé (le mien depuis l'état local,
  // ceux des autres depuis les données chargées) + synergies d'équipe.
  const fieldAssets = React.useMemo<FieldAsset[]>(() => {
    const myCard = cardState.active[0];
    return leagueMembers
      .map((m) => ({
        sender: m.name,
        isMe: m.isMe,
        cardId: m.isMe ? myCard : m.sentCardId,
      }))
      .filter((f): f is FieldAsset => !!f.cardId);
  }, [leagueMembers, cardState.active]);

  const teamCombos = React.useMemo(
    () => getActiveCombos(fieldAssets.map((f) => f.cardId)),
    [fieldAssets]
  );

  // Seed initial + reset quotidien du shop (et des slots non verrouillés).
  React.useEffect(() => {
    if (!player || !hasJoined) return;
    const current = player.league_cards;
    if (!current) {
      savePlayer({
        ...player,
        league_gold: player.league_gold ?? STARTER_LEAGUE_GOLD,
        league_cards: initCardState(player.name),
      });
      return;
    }
    const ensured = ensureDailyShop(current, player.name);
    if (ensured !== current) {
      savePlayer({ ...player, league_cards: ensured });
    }
  }, [player, hasJoined, savePlayer]);

  // ── Derive timeline + ratings from currentMatch ─────────────────────────
  const { timeline, ratings } = React.useMemo(() => {
    if (!currentMatch || !leagueMembers.length)
      return { timeline: [] as MatchAction[], ratings: [] as PlayerRating[] };

    const oppSeed =
      typeof currentMatch.id === "string"
        ? parseInt(currentMatch.id.replace(/\D/g, "")) || 1
        : (currentMatch.id as number) || 1;
    const opp = generateBotTeam(1, oppSeed);
    const result = simulateMatch(
      {
        name: "Mon Équipe",
        members: leagueMembers,
        goalkeeperLevel,
        activePower: undefined,
      },
      {
        name: currentMatch.opponent_name || opp.name,
        members: opp.members,
        goalkeeperLevel: opp.goalkeeperLevel,
      },
      Math.abs((oppSeed * 131071 + leagueMembers.reduce((s, m) => s + m.power, 0) * 31) | 0)
    );
    return { timeline: result.timeline, ratings: result.ratings };
  }, [currentMatch, leagueMembers, goalkeeperLevel]);

  // ── Actions ─────────────────────────────────────────────────────────────
  const joinLeague = React.useCallback(async (teamName?: string) => {
    if (!player?.name) return;
    setHasJoined(true);
    savePlayer({ ...player, has_joined_league: true, league_team: teamName ?? player.league_team ?? "Hyundai FC" });
  }, [player, savePlayer]);

  const leaveLeague = React.useCallback(() => {
    if (!player) return;
    leagueCache = null;
    setHasJoined(false);
    savePlayer({ ...player, has_joined_league: false, league_team: undefined });
  }, [player, savePlayer]);

  // Sandbox de test (non sauvegardé, sans récompense) : relance le moteur avec
  // un seed aléatoire et le pouvoir choisi, renvoie le résultat brut.
  const runTestMatch = React.useCallback(
    (powerId?: string): MatchSimResult => {
      const opp = generateBotTeam(1, todayOpponentSeed());
      const power = powerId
        ? (player?.league_powers ?? []).find((p) => p.id === powerId) ?? null
        : null;
      return simulateMatch(
        { name: "Mon Équipe", members: leagueMembers, goalkeeperLevel, activePower: power },
        { name: opp.name, members: opp.members, goalkeeperLevel: opp.goalkeeperLevel }
      );
    },
    [leagueMembers, goalkeeperLevel, player?.league_powers]
  );

  const runMatch = React.useCallback(async () => {
    if (!player?.name || !hasJoined) return;

    const daySeed = todayOpponentSeed();
    const opp = generateBotTeam(1, daySeed);
    const sim = simulateMatch(
      {
        name: "Mon Équipe",
        members: leagueMembers,
        goalkeeperLevel,
        activePower,
      },
      {
        name: opp.name,
        members: opp.members,
        goalkeeperLevel: opp.goalkeeperLevel,
      },
      Math.abs((daySeed * 131071 + totalTeamPower * 31) | 0)
    );

    const record: Omit<LeagueMatchRecord, "id"> = {
      result: sim.result,
      home_score: sim.homeScore,
      away_score: sim.awayScore,
      points: sim.points,
      opponent_name: sim.opponentName,
      team_power: sim.teamPower,
      opp_power: sim.oppPower,
      created_at: new Date().toISOString(),
      mvp: sim.mvp,
    };

    setMatchHistory((prev) =>
      currentMatch ? [currentMatch, ...prev] : prev
    );
    setCurrentMatch({ ...record, id: `match_${Date.now()}` });
    setShowRatingsPopup(false);
    isSimRef.current = true;
    simSecRef.current = 0;
    setMatchSecondsElapsed(0);

    // ── Récompense en Golds de ligue (+ effets de cartes VIP) ──────────────
    const activeIds = player.league_cards?.active ?? [];
    const mods = computeCardModifiers(activeIds);
    let goldReward = MATCH_GOLD_REWARD[sim.result];
    if (activeIds.includes("celebrite_loge")) {
      // Célébrité en Loge : gains doublés si victoire, 0 si défaite.
      goldReward =
        sim.result === "win"
          ? goldReward * 2
          : sim.result === "loss"
          ? 0
          : goldReward;
    }
    goldReward += mods.flatGold; // Sponsor Qatari : +2 fixes
    const newGold = (player.league_gold ?? STARTER_LEAGUE_GOLD) + goldReward;

    // Récompense en orbes : 1 ROUGE + 1 BLEUE après chaque match.
    const stamp = Date.now();
    const newAuras: Aura[] = [
      ...(player.auras ?? []),
      { id: `orb_${stamp}_r`, type: "ROUGE" },
      { id: `orb_${stamp}_b`, type: "BLEUE" },
    ];

    // Consume active power + sauvegarde des golds et des orbes
    if (activePower) setActivePower(null);
    savePlayer({
      ...player,
      league_gold: newGold,
      auras: newAuras,
      ...(activePower ? { active_power: null } : {}),
    });

    try {
      await sb.insert("league_matches", record);
    } catch (e) {
      console.warn("useLeague: erreur sauvegarde match", e);
    }
  }, [
    player,
    hasJoined,
    leagueMembers,
    goalkeeperLevel,
    activePower,
    currentMatch,
    savePlayer,
  ]);

  const upgradeGoalkeeper = React.useCallback(
    async (amount: number) => {
      if (!player) return;
      const newContrib =
        (player.goalkeeper_contribution ?? 0) + amount;
      const newSpent = player.spent + amount;
      const updated: Player = {
        ...player,
        goalkeeper_contribution: newContrib,
        spent: newSpent,
      };
      savePlayer(updated);
      try {
        await sb.upsert("players", {
          name: player.name,
          goalkeeper_contribution: newContrib,
          spent: newSpent,
        });
      } catch (e) {
        console.warn("useLeague: erreur upgrade goalkeeper", e);
      }
    },
    [player, savePlayer]
  );

  const craftAuras = React.useCallback(
    (a: AuraType, b: AuraType): Power | null => {
      if (!player) return null;
      const auras: Aura[] = [...(player.auras ?? [])];

      const removeOne = (type: AuraType): boolean => {
        const idx = auras.findIndex((x) => x.type === type);
        if (idx === -1) return false;
        auras.splice(idx, 1);
        return true;
      };

      if (!removeOne(a) || !removeOne(b)) return null;

      const power = craftPower(a, b, bettingPoints);
      if (!power) return null;

      const updated: Player = { ...player, auras, active_power: power };
      savePlayer(updated);
      setActivePower(power);
      return power;
    },
    [player, savePlayer, bettingPoints]
  );

  // Fusion d'orbes → ajoute le pouvoir à l'inventaire et consomme les orbes
  const craftPowerFromOrbs = React.useCallback(
    (a: AuraType, b: AuraType): Power | null => {
      if (!player) return null;
      const auras: Aura[] = [...(player.auras ?? [])];

      const removeOne = (type: AuraType): boolean => {
        const idx = auras.findIndex((x) => x.type === type);
        if (idx === -1) return false;
        auras.splice(idx, 1);
        return true;
      };

      // Retire les deux orbes requis (gère le cas même couleur ×2)
      if (!removeOne(a) || !removeOne(b)) return null;

      const power = craftPower(a, b, bettingPoints);
      if (!power) return null;

      savePlayer({
        ...player,
        auras,
        league_powers: [...(player.league_powers ?? []), power],
      });
      return power;
    },
    [player, savePlayer, bettingPoints]
  );

  // Craft d'une recette spéciale (déblocage par points géré côté UI).
  const craftPremiumRecipe = React.useCallback(
    (recipeId: string): Power | null => {
      if (!player) return null;
      const recipe = PREMIUM_RECIPES.find((r) => r.id === recipeId);
      if (!recipe) return null;

      const auras: Aura[] = [...(player.auras ?? [])];
      for (const t of ALL_AURA_TYPES) {
        let need = recipe.cost[t];
        while (need-- > 0) {
          const idx = auras.findIndex((x) => x.type === t);
          if (idx === -1) return null; // orbes insuffisants
          auras.splice(idx, 1);
        }
      }

      const power = makePremiumPower(recipe, bettingPoints);
      savePlayer({
        ...player,
        auras,
        league_powers: [...(player.league_powers ?? []), power],
      });
      return power;
    },
    [player, savePlayer, bettingPoints]
  );

  const assignPowerToMatch = React.useCallback(
    (matchIndex: number, powerId: string) => {
      if (!player) return;
      savePlayer({
        ...player,
        match_powers: { ...(player.match_powers ?? {}), [matchIndex]: powerId },
      });
    },
    [player, savePlayer]
  );

  const unassignMatch = React.useCallback(
    (matchIndex: number) => {
      if (!player) return;
      const mp = { ...(player.match_powers ?? {}) };
      delete mp[matchIndex];
      savePlayer({ ...player, match_powers: mp });
    },
    [player, savePlayer]
  );

  // ── Actions cartes (achat / reroll / activation / verrou) ────────────────
  const buyCard = React.useCallback(
    (cardId: string) => {
      if (!player) return;
      const def = CARD_BY_ID[cardId];
      if (!def) return;
      const cs = ensureDailyShop(
        player.league_cards ?? initCardState(player.name),
        player.name
      );
      if (!cs.shop.offers.includes(cardId)) return;
      const cost = CARD_COST[def.tier];
      const gold = player.league_gold ?? STARTER_LEAGUE_GOLD;
      if (gold < cost) return;
      // Inventaire plein → achat bloqué.
      const owned = Object.values(cs.inventory).reduce((a, b) => a + b, 0);
      if (owned >= MAX_INVENTORY) return;
      savePlayer({
        ...player,
        league_gold: gold - cost,
        league_cards: {
          ...cs,
          inventory: { ...cs.inventory, [cardId]: (cs.inventory[cardId] ?? 0) + 1 },
          shop: {
            ...cs.shop,
            // Remplace l'offre achetée par un emplacement vide (placeholder UI).
            offers: cs.shop.offers.map((o) => (o === cardId ? "" : o)),
          },
        },
      });
    },
    [player, savePlayer]
  );

  // Revente d'une carte de l'inventaire (rembourse SELL_REFUND, retire 1 ex.).
  const sellCard = React.useCallback(
    (cardId: string) => {
      if (!player) return;
      const def = CARD_BY_ID[cardId];
      if (!def) return;
      const cs = ensureDailyShop(
        player.league_cards ?? initCardState(player.name),
        player.name
      );
      const qty = cs.inventory[cardId] ?? 0;
      if (qty <= 0) return;
      const nextInv = { ...cs.inventory };
      if (qty <= 1) delete nextInv[cardId];
      else nextInv[cardId] = qty - 1;
      // Si la carte vendue était sur le terrain et qu'on n'en a plus, la retirer.
      const stillOwns = (nextInv[cardId] ?? 0) > 0;
      const nextActive =
        cs.active[0] === cardId && !stillOwns ? [] : cs.active;
      savePlayer({
        ...player,
        league_gold:
          (player.league_gold ?? STARTER_LEAGUE_GOLD) + SELL_REFUND[def.tier],
        league_cards: { ...cs, inventory: nextInv, active: nextActive },
      });
    },
    [player, savePlayer]
  );

  // Achat d'une orbe au shop (classiques 1 Gold, verte 2).
  const buyOrb = React.useCallback(
    (type: AuraType): boolean => {
      if (!player) return false;
      const cost = ORB_COST[type];
      const gold = player.league_gold ?? STARTER_LEAGUE_GOLD;
      if (gold < cost) return false;
      savePlayer({
        ...player,
        league_gold: gold - cost,
        auras: [
          ...(player.auras ?? []),
          { id: `orb_${Date.now()}_${type[0].toLowerCase()}`, type },
        ],
      });
      return true;
    },
    [player, savePlayer]
  );

  const rerollShop = React.useCallback(() => {
    if (!player) return;
    const cs = ensureDailyShop(
      player.league_cards ?? initCardState(player.name),
      player.name
    );
    // Combo « Business Club » (2 VIP sur le terrain d'équipe) : 1er reroll gratuit.
    const businessClub = teamCombos.some(
      (c) => c.family === "vip" && c.tier2
    );
    const free = businessClub && cs.shop.rerollsUsed === 0;
    const cost = free ? 0 : REROLL_COST;
    const gold = player.league_gold ?? STARTER_LEAGUE_GOLD;
    if (gold < cost) return;
    savePlayer({
      ...player,
      league_gold: gold - cost,
      league_cards: {
        ...cs,
        shop: {
          ...cs.shop,
          offers: rollShop(Math.random),
          rerollsUsed: cs.shop.rerollsUsed + 1,
        },
      },
    });
  }, [player, savePlayer, teamCombos]);

  // Un seul atout sur le terrain à la fois : envoyer remplace, re-cliquer retire.
  const sendToField = React.useCallback(
    (cardId: string) => {
      if (!player) return;
      const cs = ensureDailyShop(
        player.league_cards ?? initCardState(player.name),
        player.name
      );
      if ((cs.inventory[cardId] ?? 0) <= 0) return;
      const active = cs.active[0] === cardId ? [] : [cardId];
      savePlayer({ ...player, league_cards: { ...cs, active } });
    },
    [player, savePlayer]
  );

  const setCardsLocked = React.useCallback(
    (locked: boolean) => {
      if (!player) return;
      const cs = ensureDailyShop(
        player.league_cards ?? initCardState(player.name),
        player.name
      );
      savePlayer({ ...player, league_cards: { ...cs, locked } });
    },
    [player, savePlayer]
  );

  return {
    hasJoined,
    leagueMembers,
    loading,
    ready,
    standings,
    myRank,
    resultByRound,
    scoreByRound,
    fixtureByRound,
    currentMatch,
    matchHistory,
    countdown,
    matchSecondsElapsed,
    showRatingsPopup,
    setShowRatingsPopup,
    totalTeamPower,
    realPlayersCount,
    botCount,
    goalkeeperLevel,
    wins,
    draws,
    losses,
    played,
    points,
    winrate,
    timeline,
    ratings,
    activePower,
    setActivePower,
    joinLeague,
    runMatch,
    runTestMatch,
    upgradeGoalkeeper,
    craftAuras,
    loadLeagueData,
    orbCounts,
    powers,
    matchPowers,
    schedule,
    preparedCount,
    elo,
    tier,
    tierIndex,
    nextTier,
    daysLeft,
    matchesDone,
    craftPowerFromOrbs,
    craftPremiumRecipe,
    assignPowerToMatch,
    unassignMatch,
    leagueGold,
    cardState,
    fieldAssets,
    teamCombos,
    buyCard,
    sellCard,
    buyOrb,
    rerollShop,
    sendToField,
    setCardsLocked,
    leaveLeague,
  };
}
